import React, { useState } from "react";
import useGetRequest from "../../hooks/useGetRequest";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Typography,
  Tooltip, // добавил импорт Tooltip
} from "antd";
import { ColumnsType } from "antd/es/table";
import { IMeterReading } from "../../types/meterReading.type";
import { Currency } from "../../types/currency.type";
import { api } from "../../api/api";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { canEditMeterReadings } from "../../utils/roleUtils";

const { Title } = Typography;

export default function MeterReadings() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { data, loading, refresh } = useGetRequest("/meter-readings");
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedMeterReading, setSelectedMeterReading] =
    useState<IMeterReading | null>(null);
  const [form] = Form.useForm();

  const handleSubmitForm = async (values: {
    name: string;
    currency?: Currency;
  }) => {
    setConfirmLoading(true);
    try {
      if (selectedMeterReading) {
        await api.put(`/meter-readings/${selectedMeterReading._id}`, values);
        message.success("Счетчик успешно обновлен.");
      } else {
        await api.post("/meter-readings", values);
        message.success("Счетчик успешно добавлен.");
      }

      refresh();
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Error saving meter reading:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleOpenModal = (reading?: IMeterReading) => {
    if (reading) {
      setSelectedMeterReading(reading);
      setIsModalOpen(true);
      form.setFieldsValue(reading);
    } else {
      setIsModalOpen(true);
      form.resetFields();
      setSelectedMeterReading(null);
    }
  };

  const columns: ColumnsType<IMeterReading> = [
    {
      title: "Название",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Баланс",
      key: "balance",
      dataIndex: "balance",
      render: (balance: number) => (
        <Tooltip title={balance < 0 ? "Наша предоплата" : balance > 0 ? "Мы должны" : undefined}>
          <span style={{ color: balance < 0 ? '#52c41a' : balance > 0 ? '#faad14' : undefined }}>
            {balance}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Валюта",
      key: "currency",
      dataIndex: "currency",
    },
    {
      title: "Цена",
      key: "price",
      dataIndex: "price",
    },
    {
      title: "Последнее показание",
      key: "lastReading",
      dataIndex: "lastReading",
    },
    {
      title: "Создал",
      key: "ownerId",
      dataIndex: "ownerId",
      render: (ownerId: { username: string }) => ownerId.username,
    },
    {
      title: "",
      key: "actions",
      render: (_: any, reading: IMeterReading) => {
        const menu = (
          <Menu>
                              {canEditMeterReadings(authState.user?.role) && (
                    <Menu.Item onClick={() => handleOpenModal(reading)}>
                      Изменить
                    </Menu.Item>
                  )}
            <Menu.Item
              onClick={() =>
                navigate(
                  `/meter-readings/records/${reading._id}/${reading.name}`
                )
              }
            >
              Записи
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Счетчикы</Title>
      {canEditMeterReadings(authState.user?.role) && (
        <Button
          type="primary"
          style={{ marginBottom: 16 }}
          onClick={() => handleOpenModal()}
        >
          Добавить
        </Button>
      )}

      <Table
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record) => record._id}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={selectedMeterReading ? "Изменить" : "Добавить"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitForm}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите Название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="price"
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name="balance"
            label="Баланс"
            rules={[{ required: true, message: "Введите баланс" }]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            name="lastReading"
            label="Последнее показание"
            rules={[{ required: true, message: "Введите последнее показание" }]}
          >
            <InputNumber />
          </Form.Item>
          {!selectedMeterReading && (
            <Form.Item
              name="currency"
              label="Валюта"
              rules={[{ required: true, message: "Выберите валюту" }]}
            >
              <Select placeholder="Выберите валюту">
                {Object.values(Currency).map((currency) => (
                  <Select.Option key={currency} value={currency}>
                    {currency}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
